import React, { useState, useEffect, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { supabase } from "../../config/supabase"; // Importação do Supabase
import { Search, Eye, Download, Grid2x2 as Grid, List, Plus, Trash2, ArrowUpDown, ArrowUp, ArrowDown, RefreshCw, Pencil, Stethoscope, FileText, DollarSign, Activity } from "lucide-react";
import { toast } from "react-toastify";
import { useAuth } from "../../context/AuthContext";
import EditPatientModal from "../../components/admin/EditPatientModal";
import LinkedPatientBadge from "../../components/admin/LinkedPatientBadge";
import PatientRegistrationModal from "../../components/common/PatientRegistrationModal";
import { createPatientRegistration, isDuplicateCpfError } from "../../services/PatientRegistrationService";
import { PatientData, NewPatientForm } from "../../types/supabase"; // Novos tipos
import { PatientLifecycleEvent, PatientLifecycleService } from "../../services/PatientLifecycleService";
import { buildTenantPath, extractTenantSlugFromPathname } from "../../utils/tenantSlug";
import {
  calculateIMC,
  createEmptyNewPatientForm,
  getIMCClassification,
  validatePatientEmail,
  shouldConfirmLowPhysicalData,
  validatePatientDocuments,
  validatePatientPhone,
} from "../../utils/patientRegistration";
import { getMedicalRecordLifecycleStatus } from "../../utils/medicalRecordStatus";
import { isClinicalHistoryRecord } from "../../utils/clinicalDocuments";
import ActionButton from "../../components/ui/ActionButton";
import { matchesPatientSearch } from "../../utils/patientSearch";

type PatientRegistrationRoutePrefix = 'admin' | 'fisioterapeuta';

interface PatientRegistrationPermissions {
  canCreatePatient?: boolean;
  canEditPatient?: boolean;
  canArchivePatient?: boolean;
  canRestorePatient?: boolean;
  canExportPatients?: boolean;
  canViewArchivedPatients?: boolean;
  canOpenFinancial?: boolean;
}

interface PatientRegistrationPageProps {
  routePrefix?: PatientRegistrationRoutePrefix;
  permissions?: PatientRegistrationPermissions;
}

const DEFAULT_PATIENT_REGISTRATION_PERMISSIONS: Required<PatientRegistrationPermissions> = {
  canCreatePatient: true,
  canEditPatient: true,
  canArchivePatient: true,
  canRestorePatient: true,
  canExportPatients: true,
  canViewArchivedPatients: true,
  canOpenFinancial: true,
};

const PatientRegistrationPage: React.FC<PatientRegistrationPageProps> = ({
  routePrefix = 'admin',
  permissions,
}) => {
  const access = {
    ...DEFAULT_PATIENT_REGISTRATION_PERMISSIONS,
    ...permissions,
  };
  const { user, selectedClient } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const activeClientId = selectedClient?.clientId || user?.current_client_id || null;
  const tenantSlug = selectedClient?.clientSlug || extractTenantSlugFromPathname(location.pathname);
  const prontuarioPath = `/${routePrefix}/prontuario`;
  const assessmentsPath = routePrefix === 'admin'
    ? '/admin/assessments-v2'
    : '/fisioterapeuta/avaliacoes';
  const [patients, setPatients] = useState<PatientData[]>([]);
  const [allPatients, setAllPatients] = useState<PatientData[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPatient, setSelectedPatient] = useState<PatientData | null>(null);
  const [viewMode, setViewMode] = useState<'table' | 'cards'>('table');
  const [editingPatient, setEditingPatient] = useState<PatientData | null>(null);
  const [showNewPatientModal, setShowNewPatientModal] = useState(false);
  const [newPatientForm, setNewPatientForm] = useState<NewPatientForm>(createEmptyNewPatientForm());

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [consentGiven, setConsentGiven] = useState(false);
  const [sortColumn, setSortColumn] = useState<string>('nome');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [deletingPatient, setDeletingPatient] = useState<PatientData | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastSyncTime, setLastSyncTime] = useState<Date | null>(null);
  const [patientViewMode, setPatientViewMode] = useState<'active' | 'archived'>('active');
  const [deleteReason, setDeleteReason] = useState('');
  const [restoringPatient, setRestoringPatient] = useState<PatientData | null>(null);
  const [restoreReason, setRestoreReason] = useState('');
  const [isRestoring, setIsRestoring] = useState(false);
  const [lifecycleEvents, setLifecycleEvents] = useState<PatientLifecycleEvent[]>([]);
  const [loadingLifecycleEvents, setLoadingLifecycleEvents] = useState(false);
  const [patientSummary, setPatientSummary] = useState<{
    loading: boolean;
    evolutions: number;
    assessments: number;
    activeSessions: number;
    packageStatus: string;
  }>({
    loading: false,
    evolutions: 0,
    assessments: 0,
    activeSessions: 0,
    packageStatus: 'Sem pacote',
  });

  const getLifecycleStatus = (patient: PatientData): 'active' | 'inactive' | 'archived' => {
    const status = patient.patientLifecycleStatus;
    if (status === 'inactive' || status === 'archived') {
      return status;
    }
    return 'active';
  };

  const filterPatientsByView = (items: PatientData[]) => {
    return items.filter((patient) => {
      const status = getLifecycleStatus(patient);
      if (!access.canViewArchivedPatients) {
        return status !== 'archived';
      }
      if (patientViewMode === 'archived') {
        return status === 'archived';
      }
      return status !== 'archived';
    });
  };

  const handleCepBlur = async (cep: string) => {
    const cleanedCep = cep.replace(/\D/g, "");
    if (cleanedCep.length !== 8) {
      return;
    }

    try {
      const response = await fetch(`https://viacep.com.br/ws/${cleanedCep}/json/`);
      const data = await response.json();

      if (data.erro) {
        toast.error("CEP não encontrado.");
        return;
      }

      setNewPatientForm(prev => ({
        ...prev,
        patientAddressStreet: data.logradouro,
        patientAddressNeighborhood: data.bairro,
        patientAddressCity: data.localidade,
        patientAddressState: data.uf,
      }));

    } catch (error) {
      toast.error("Erro ao buscar CEP. Verifique sua conexão.");
      console.error("Erro ao buscar CEP:", error);
    }
  };

  const DATE_ONLY_REGEX = /^(\d{4})-(\d{2})-(\d{2})$/;

  const parseDateValue = (dateValue: string | Date | null | undefined): Date | null => {
    if (!dateValue) return null;

    if (dateValue instanceof Date) {
      return isNaN(dateValue.getTime()) ? null : dateValue;
    }

    if (typeof dateValue !== 'string') {
      return null;
    }

    const trimmedValue = dateValue.trim();
    if (!trimmedValue) return null;

    const dateOnlyMatch = trimmedValue.match(DATE_ONLY_REGEX);
    if (dateOnlyMatch) {
      const year = Number(dateOnlyMatch[1]);
      const month = Number(dateOnlyMatch[2]);
      const day = Number(dateOnlyMatch[3]);
      const localDate = new Date(year, month - 1, day);
      const isValidDate =
        localDate.getFullYear() === year &&
        localDate.getMonth() === month - 1 &&
        localDate.getDate() === day;

      return isValidDate ? localDate : null;
    }

    const parsedDate = new Date(trimmedValue);
    return isNaN(parsedDate.getTime()) ? null : parsedDate;
  };

  // Função para calcular idade
  const calculateAge = (birthDate: string): number => {
    if (!birthDate) return 0;
    
    try {
      const birth = parseDateValue(birthDate);
      if (!birth) return 0;

      const today = new Date();
      let age = today.getFullYear() - birth.getFullYear();
      const monthDiff = today.getMonth() - birth.getMonth();
      
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
        age--;
      }
      
      return age;
    } catch (error) {
      console.error("Erro ao calcular idade:", error);
      return 0;
    }
  };

  // Função para formatar data
  const formatDate = (dateValue: string | Date | null | undefined, includeTime: boolean = false): string => {
    if (!dateValue) return 'N/A';
    
    try {
      const date = parseDateValue(dateValue);
      if (!date) return 'N/A';
      
      if (includeTime) {
        return date.toLocaleString('pt-BR');
      } else {
        return date.toLocaleDateString('pt-BR');
      }
    } catch (error) {
      console.error('Erro ao formatar data:', error);
      return 'N/A';
    }
  };

  const formatAddress = (patient: PatientData): string => {
    const parts = [
      patient.patientAddressStreet,
      patient.patientAddressNumber,
      patient.patientAddressComplement,
      patient.patientAddressNeighborhood,
      patient.patientAddressCity,
      patient.patientAddressState,
      patient.patientAddressZipCode,
    ]
      .map((value) => (typeof value === 'string' ? value.trim() : value))
      .filter(Boolean);

    return parts.length > 0 ? parts.join(', ') : 'Não informado';
  };

  // Função para salvar edição do paciente
  const handleSavePatient = async (patientId: string, updatedData: Partial<PatientData>) => {
    if (!access.canEditPatient) {
      toast.error("Seu perfil não possui permissão para editar pacientes.");
      throw new Error("Perfil sem permissão para editar pacientes");
    }

    if (!activeClientId) {
      toast.error("Cliente não identificado. Impossível editar paciente.");
      throw new Error("Cliente não identificado para edição do paciente");
    }

    try {
      const { data: patientLink, error: patientLinkError } = await supabase
        .from("patient_clients")
        .select("patientId")
        .eq("patientId", patientId)
        .eq("clientId", activeClientId)
        .maybeSingle();

      if (patientLinkError && patientLinkError.code !== "PGRST116") {
        console.error("Erro ao validar vínculo do paciente com o tenant:", patientLinkError);
        throw patientLinkError;
      }

      if (!patientLink) {
        toast.error("Paciente não pertence ao tenant selecionado.");
        throw new Error("Tentativa de editar paciente fora do escopo do tenant");
      }

      const { error } = await supabase
        .from("patients")
        .update(updatedData)
        .eq("patientId", patientId);

      if (error) {
        console.error("Erro ao atualizar paciente no Supabase:", error);
        throw error;
      }
      fetchPatients(); // Atualiza a lista
    } catch (error) {
      console.error("Erro ao atualizar paciente:", error);
      throw error;
    }
  };

  // Função para criar novo paciente
  const handleCreatePatient = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!access.canCreatePatient) {
      toast.error("Seu perfil não possui permissão para criar pacientes.");
      return;
    }
    
    if (!user) {
      toast.error('Usuário não autenticado');
      return;
    }

    if (!activeClientId) {
      toast.error('Cliente não identificado');
      return;
    }

    // Validações básicas
    if (!newPatientForm.nome.trim() || !newPatientForm.sobrenome.trim()) {
      toast.error('Nome e sobrenome são obrigatórios');
      return;
    }

    if (!newPatientForm.data_x0020_de_x0020_nascimento) {
      toast.error('Data de nascimento é obrigatória');
      return;
    }

    if (!newPatientForm.sexo) {
      toast.error('Sexo é obrigatório');
      return;
    }

    if (newPatientForm.altura_x0020__x0028_cm_x0029_ <= 0 || newPatientForm.peso <= 0) {
      toast.error("Altura e peso devem ser maiores que zero");
      return;
    }

    if (!shouldConfirmLowPhysicalData(newPatientForm.altura_x0020__x0028_cm_x0029_, newPatientForm.peso)) {
      toast.info("Cadastro cancelado para revisão de altura e peso.");
      return;
    }

    const documentsValidationError = validatePatientDocuments(
      newPatientForm.patientCpf,
      newPatientForm.patientRg,
    );

    if (documentsValidationError) {
      toast.error(documentsValidationError);
      return;
    }

    const emailValidationError = validatePatientEmail(newPatientForm.patientEmail);
    if (emailValidationError) {
      toast.error(emailValidationError);
      return;
    }

    const phoneValidationError = validatePatientPhone(newPatientForm.patientPhone);
    if (phoneValidationError) {
      toast.error(phoneValidationError);
      return;
    }

    try {
      setIsSubmitting(true);
      const result = await createPatientRegistration({
        clientId: activeClientId,
        formData: newPatientForm,
      });
      
      if (result.action === "linked") {
        toast.success("Paciente já cadastrado no sistema e vinculado a esta clínica.");
      } else if (result.action === "already_linked") {
        toast.info("Paciente já estava vinculado a esta clínica.");
      } else {
        toast.success("Paciente cadastrado com sucesso!");
      }
      setShowNewPatientModal(false);
      fetchPatients(); // Atualiza a lista
      setNewPatientForm(createEmptyNewPatientForm());
      setConsentGiven(false); // Reset consent checkbox
    } catch (error) {
      console.error('Erro ao criar paciente:', error);

      if (isDuplicateCpfError(error)) {
        toast.error("CPF já cadastrado. Informe outro CPF.");
        return;
      }

      toast.error('Erro ao cadastrar paciente');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Função para atualizar formulário de novo paciente
  const updateNewPatientForm = (field: keyof NewPatientForm, value: string | number) => {
    setNewPatientForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleCloseNewPatientModal = () => {
    setShowNewPatientModal(false);
  };

  const handleCancelNewPatientModal = () => {
    setShowNewPatientModal(false);
    setConsentGiven(false);
  };

  const fetchPatients = useCallback(async () => {
    if (!activeClientId) {
      toast.error("Cliente não identificado. Impossível carregar pacientes.");
      setAllPatients([]);
      setPatients([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const { data: patientClients, error } = await supabase
        .from("patient_clients")
        .select("*, patients!inner(*)")
        .eq("clientId", activeClientId)
        .order("patientFullName", { referencedTable: "patients", ascending: true });

      if (error) {
        console.error("Erro ao carregar pacientes do Supabase:", error);
        throw error;
      }

      const mappedPatients: PatientData[] = patientClients.map(pc => {
        const patient = pc.patients;
        if (!patient) return null;

        return {
          ...patient,
          clientId: pc.clientId,
          diagn_x00f3_sticos: patient.patientMedicalInfoConditions?.join(", "),
        }
      }).filter((p): p is PatientData => p !== null);

      setAllPatients(mappedPatients);
    } catch (error: any) {
      console.error("Erro ao carregar pacientes:", error.message);
      toast.error("Erro ao conectar com o banco de dados ou carregar pacientes");
    } finally {
      setLoading(false);
    }
  }, [activeClientId]);

  // Carregar pacientes e configurar real-time subscription
  useEffect(() => {
    fetchPatients();

    if (!activeClientId) return;

    let isSubscribed = false;
    let shouldRemoveAfterSubscribe = false;

    const channel = supabase
      .channel(`public:patients:${activeClientId}`)
      .on("postgres_changes", 
         { event: "*", schema: "public", table: "patients", filter: `clientId=eq.${activeClientId}` },
         (payload) => {
           console.log("Change received! Refetching data...", payload);
           fetchPatients();
         }
      )
      .subscribe((status) => {
        if (status !== 'SUBSCRIBED') return;
        if (shouldRemoveAfterSubscribe) {
          void supabase.removeChannel(channel);
          return;
        }
        isSubscribed = true;
      });

    return () => {
      shouldRemoveAfterSubscribe = true;
      if (isSubscribed) void supabase.removeChannel(channel);
    };
  }, [activeClientId, fetchPatients]);

  const loadLifecycleEvents = useCallback(async (patientId: string) => {
    if (!activeClientId || !access.canViewArchivedPatients) {
      setLifecycleEvents([]);
      return;
    }

    try {
      setLoadingLifecycleEvents(true);
      const events = await PatientLifecycleService.getLifecycleEvents(patientId, activeClientId);
      setLifecycleEvents(events);
    } catch (error: any) {
      console.warn("Não foi possível carregar eventos de ciclo de vida:", error?.message || error);
      setLifecycleEvents([]);
    } finally {
      setLoadingLifecycleEvents(false);
    }
  }, [activeClientId, access.canViewArchivedPatients]);

  const loadPatientSummary = useCallback(async (patientId: string) => {
    if (!activeClientId) return;

    setPatientSummary(prev => ({ ...prev, loading: true }));

    try {
      const [medicalRecordsResult, assessmentsResult] = await Promise.all([
        supabase
          .from('medical_records')
          .select('recordId, recordType, recordAttachments')
          .eq('clientId', activeClientId)
          .eq('patientId', patientId),
        supabase
          .from('assessment_results_v2')
          .select('resultId', { count: 'exact', head: true })
          .eq('clientId', activeClientId)
          .eq('patientId', patientId),
      ]);

      const activeMedicalRecords = (medicalRecordsResult.data || []).filter((record: any) =>
        getMedicalRecordLifecycleStatus(record.recordType, record.recordAttachments) === 'active' &&
        !isClinicalHistoryRecord(record.recordAttachments)
      ).length;

      let activeSessions = 0;
      let packageStatus = 'Sem pacote';

      if (access.canOpenFinancial) {
        const { data: financialProfilesData } = await supabase
          .from('financial_profiles')
          .select('financialProfileId, financialProfileIsActive, financialProfileHasPackage')
          .eq('clientId', activeClientId)
          .eq('patientId', patientId);

        const financialProfileIds = (financialProfilesData || []).map((row: any) => row.financialProfileId).filter(Boolean);

        if (financialProfilesData && financialProfilesData.length > 0) {
          const profile = financialProfilesData[0] as any;
          packageStatus = profile.financialProfileIsActive ? 'Ativo' : 'Inativo';

          if (profile.financialProfileHasPackage && financialProfileIds.length > 0) {
            const { data: packagesData } = await supabase
              .from('packages')
              .select('packageSessionsRemaining, packageSessionsUsed, packageSessionsContracted, packageName')
              .in('financialProfileId', financialProfileIds)
              .eq('clientId', activeClientId);

            activeSessions = (packagesData || []).reduce((sum: number, pkg: any) => sum + (Number(pkg.packageSessionsRemaining) || 0), 0);
            if (packagesData && packagesData.length > 0) {
              const pkg = packagesData[0] as any;
              packageStatus = `${pkg.packageName || 'Pacote'} (${pkg.packageSessionsUsed || 0}/${pkg.packageSessionsContracted || 0})`;
            }
          }
        }
      }

      setPatientSummary({
        loading: false,
        evolutions: activeMedicalRecords,
        assessments: assessmentsResult.count || 0,
        activeSessions,
        packageStatus,
      });
    } catch (error) {
      console.error('Erro ao carregar resumo do paciente:', error);
      setPatientSummary({
        loading: false,
        evolutions: 0,
        assessments: 0,
        activeSessions: 0,
        packageStatus: 'Indisponível',
      });
    }
  }, [activeClientId, access.canOpenFinancial]);

  useEffect(() => {
    if (!selectedPatient?.patientId) {
      setLifecycleEvents([]);
      setPatientSummary(prev => ({ ...prev, loading: false }));
      return;
    }
    loadLifecycleEvents(selectedPatient.patientId);
    loadPatientSummary(selectedPatient.patientId);
  }, [selectedPatient?.patientId, loadLifecycleEvents, loadPatientSummary]);

  useEffect(() => {
    setSearchTerm('');
    setSelectedPatient(null);
    setDeletingPatient(null);
    setDeleteReason('');
    setRestoringPatient(null);
    setRestoreReason('');
    setPatients(filterPatientsByView(allPatients));
  }, [patientViewMode, allPatients, access.canViewArchivedPatients]);

  useEffect(() => {
    if (!access.canViewArchivedPatients && patientViewMode !== 'active') {
      setPatientViewMode('active');
    }
  }, [access.canViewArchivedPatients, patientViewMode]);

  // Função para ordenar pacientes
  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  // Função para obter ícone de ordenação
  const getSortIcon = (column: string) => {
    if (sortColumn !== column) {
      return <ArrowUpDown className="w-4 h-4 text-gray-400" />;
    }
    return sortDirection === 'asc' ?
      <ArrowUp className="w-4 h-4 text-primary-600" /> :
      <ArrowDown className="w-4 h-4 text-primary-600" />;
  };

  // Filtrar e ordenar pacientes
  const filteredPatients = patients
    .filter(patient => {
      return matchesPatientSearch(
        searchTerm,
        patient.patientFullName,
        [patient.patientId],
      );
    })
    .sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (sortColumn) {
        case 'prontuario':
          aValue = a.patientId || '';
          bValue = b.patientId || '';
          break;
        case 'nome':
          aValue = a.patientFullName?.toLowerCase() || '';
          bValue = b.patientFullName?.toLowerCase() || '';
          break;
        case 'data_nascimento':
          aValue = parseDateValue(a.patientDateOfBirth)?.getTime() ?? 0;
          bValue = parseDateValue(b.patientDateOfBirth)?.getTime() ?? 0;
          break;
        case 'sexo':
          aValue = a.patientGender || '';
          bValue = b.patientGender || '';
          break;
        case 'idade':
          aValue = calculateAge(a.patientDateOfBirth);
          bValue = calculateAge(b.patientDateOfBirth);
          break;
        case 'altura':
          aValue = Number(a.patientHeightCm) || 0;
          bValue = Number(b.patientHeightCm) || 0;
          break;
        case 'peso':
          aValue = Number(a.patientWeightKg) || 0;
          bValue = Number(b.patientWeightKg) || 0;
          break;
        case 'imc':
          aValue = calculateIMC(Number(a.patientWeightKg) || 0, Number(a.patientHeightCm) || 0);
          bValue = calculateIMC(Number(b.patientWeightKg) || 0, Number(b.patientHeightCm) || 0);
          break;
        case 'created':
          aValue = parseDateValue(a.patientCreatedAt)?.getTime() ?? 0;
          bValue = parseDateValue(b.patientCreatedAt)?.getTime() ?? 0;
          break;
        default:
          aValue = a.patientFullName || '';
          bValue = b.patientFullName || '';
      }

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortDirection === 'asc'
          ? aValue.localeCompare(bValue, 'pt-BR')
          : bValue.localeCompare(aValue, 'pt-BR');
      }

      return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
    });

  // Calcular estatísticas
  const stats = {
    total: patients.length,
    averageAge: patients.length > 0 
      ? Math.round(patients.reduce((sum, p) => sum + calculateAge(p.patientDateOfBirth), 0) / patients.length)
      : 0,
    averageIMC: patients.length > 0
      ? patients.reduce((sum, p) => sum + calculateIMC(Number(p.patientWeightKg) || 0, Number(p.patientHeightCm) || 0), 0) / patients.length
      : 0,
    activePatients: allPatients.filter(p => getLifecycleStatus(p) !== 'archived').length,
    archivedPatients: allPatients.filter(p => getLifecycleStatus(p) === 'archived').length
  };

  // Atualizar dados manualmente do Supabase
  const handleManualRefresh = async () => {
    try {
      setIsRefreshing(true);
      toast.info('Atualizando dados...');

      // O Supabase Realtime já lida com atualizações. Uma re-leitura forçada seria:
      if (activeClientId) {
        const { data: patientClients, error } = await supabase
          .from('patient_clients')
          .select('*, patients!inner(*)')
          .eq('clientId', activeClientId)
          .order('patientFullName', { referencedTable: 'patients', ascending: true });

        if (error) throw error;

        const mappedPatients: PatientData[] = patientClients.map(pc => {
            const patient = pc.patients;
            if (!patient) return null;

            return {
              ...patient,
              clientId: pc.clientId,
              diagn_x00f3_sticos: patient.patientMedicalInfoConditions?.join(", "),
            }
        }).filter((p): p is PatientData => p !== null);

        setAllPatients(mappedPatients);
      }

      setLastSyncTime(new Date());
      toast.success('✅ Dados atualizados com sucesso!');
    } catch (error: any) {
      console.error('❌ Erro ao atualizar dados:', error.message);
      toast.error('Erro ao atualizar dados');
    } finally {
      setIsRefreshing(false);
    }
  };

  // Função para excluir paciente
  const handleDeletePatient = async () => {
    if (!access.canArchivePatient) {
      toast.error('Seu perfil não possui permissão para arquivar ou excluir pacientes.');
      return;
    }

    if (!deletingPatient || !user || !activeClientId) {
      toast.error('Paciente não selecionado ou usuário não autenticado');
      return;
    }

    if (!deleteReason.trim()) {
      toast.error('Informe o motivo da exclusão/arquivamento.');
      return;
    }

    try {
      setIsDeleting(true);
      console.log('🗑️ Iniciando exclusão do paciente:', deletingPatient.patientId);

      const result = await PatientLifecycleService.archiveOrDelete({
        patient: deletingPatient,
        user,
        clientId: activeClientId,
        reason: deleteReason.trim(),
      });

      if (result.operation === 'archived') {
        toast.success(
          `Paciente arquivado no histórico (${result.completedAppointmentsCount} agendamento(s) concluído(s), ${result.medicalRecordsCount} prontuário(s)).`
        );
      } else {
        toast.success('Paciente excluído permanentemente (sem histórico assistencial).');
      }

      setDeletingPatient(null);
      setSelectedPatient(null);
      setDeleteReason('');
      await fetchPatients();
    } catch (error: any) {
      console.error('❌ Erro ao excluir paciente:', error.message);
      if (error?.code === '23503') {
        toast.error('Não é possível excluir paciente com histórico vinculado.');
      } else {
        toast.error('Erro ao excluir paciente');
      }
    } finally {
      setIsDeleting(false);
    }
  };

  const handleRestorePatient = async () => {
    if (!access.canRestorePatient) {
      toast.error('Seu perfil não possui permissão para restaurar pacientes.');
      return;
    }

    if (!restoringPatient || !user || !activeClientId) {
      toast.error('Paciente não selecionado ou usuário não autenticado');
      return;
    }

    try {
      setIsRestoring(true);
      await PatientLifecycleService.restoreArchived({
        patient: restoringPatient,
        user,
        clientId: activeClientId,
        reason: restoreReason.trim() || 'Restauração manual via painel de pacientes',
      });

      toast.success('Paciente restaurado com sucesso.');
      setRestoringPatient(null);
      setRestoreReason('');
      setSelectedPatient(null);
      await fetchPatients();
    } catch (error: any) {
      console.error('❌ Erro ao restaurar paciente:', error?.message || error);
      toast.error('Erro ao restaurar paciente');
    } finally {
      setIsRestoring(false);
    }
  };

  // Exportar para CSV
  const exportToCSV = () => {
    const headers = [
      'Prontuário',
      'Nome',
      'Sobrenome',
      'Data de Nascimento',
      'Sexo',
      'Idade',
      'Altura (cm)',
      'Peso (kg)',
      'IMC',
      'Classificação IMC',
      'Diagnóstico',
      'Data de Criação'
    ];

    const csvData = filteredPatients.map(patient => [
      patient.patientId || '',
      patient.patientFullName || '',
      '', // Sobrenome não é um campo separado no Supabase para exportação aqui
      formatDate(patient.patientDateOfBirth),
      patient.patientGender === 'Masculino' ? 'Masculino' : patient.patientGender === 'Feminino' ? 'Feminino' : '-',
      calculateAge(patient.patientDateOfBirth).toString(),
      (Number(patient.patientHeightCm) || 0).toString(),
      (Number(patient.patientWeightKg) || 0).toString(),
      calculateIMC(Number(patient.patientWeightKg) || 0, Number(patient.patientHeightCm) || 0).toFixed(1),
      getIMCClassification(calculateIMC(Number(patient.patientWeightKg) || 0, Number(patient.patientHeightCm) || 0)),
      patient.patientMedicalInfoConditions?.join(', ') || '',
      formatDate(patient.patientCreatedAt)
    ]);

    const csvContent = [headers, ...csvData]
      .map(row => row.map(cell => `"${cell}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `pacientes_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {patientViewMode === 'archived' ? 'Pacientes Arquivados' : 'Cadastro de Pacientes'}
          </h1>
          <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3">
            <p className="text-gray-600">
              {patientViewMode === 'archived'
                ? 'Pacientes com histórico preservado fora da operação diária'
                : 'Gerencie os pacientes cadastrados no sistema'}
            </p>
            {lastSyncTime && (
              <span className="text-xs text-gray-500">
                • Última sincronização: {lastSyncTime.toLocaleTimeString('pt-BR')}
              </span>
            )}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {access.canViewArchivedPatients && (
            <div className="inline-flex rounded-lg border border-gray-300 overflow-hidden">
              <button
                onClick={() => setPatientViewMode('active')}
                className={`px-3 py-2 text-sm font-medium ${
                  patientViewMode === 'active'
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                Ativos ({stats.activePatients})
              </button>
              <button
                onClick={() => setPatientViewMode('archived')}
                className={`px-3 py-2 text-sm font-medium border-l border-gray-300 ${
                  patientViewMode === 'archived'
                    ? 'bg-amber-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                Arquivados ({stats.archivedPatients})
              </button>
            </div>
          )}
          <ActionButton
            onClick={handleManualRefresh}
            disabled={isRefreshing}
            variant="neutral"
            className="whitespace-nowrap px-3 md:px-4 md:text-base"
            title="Atualizar dados do servidor"
          >
            <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
            <span className="hidden sm:inline">{isRefreshing ? 'Atualizando...' : 'Atualizar'}</span>
          </ActionButton>
          {access.canCreatePatient && patientViewMode === 'active' && (
            <ActionButton
              onClick={() => setShowNewPatientModal(true)}
              variant="primary"
              className="whitespace-nowrap px-3 md:px-4 md:text-base"
            >
              <Plus className="w-4 h-4" />
              <span className="hidden sm:inline">Novo Paciente</span>
            </ActionButton>
          )}
          <ActionButton
            onClick={() => setViewMode(viewMode === 'table' ? 'cards' : 'table')}
            variant="neutral"
            className="whitespace-nowrap bg-slate-100 px-3 md:px-4 md:text-base"
          >
            {viewMode === 'table' ? <Grid className="w-4 h-4" /> : <List className="w-4 h-4" />}
            <span className="hidden sm:inline">{viewMode === 'table' ? 'Cards' : 'Tabela'}</span>
          </ActionButton>
          {access.canExportPatients && (
            <ActionButton
              onClick={exportToCSV}
              variant="success"
              className="whitespace-nowrap px-3 md:px-4 md:text-base"
            >
              <Download className="w-4 h-4" />
              <span className="hidden sm:inline">Exportar CSV</span>
            </ActionButton>
          )}
        </div>
      </div>

      {/* Busca */}
      <div className="bg-white p-6 rounded-lg shadow-sm border">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder={patientViewMode === 'archived' ? "Buscar arquivado por nome ou prontuário..." : "Buscar por nome ou prontuário..."}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* Lista de Pacientes */}
      {viewMode === 'table' ? (
        <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                    onClick={() => handleSort('patientFullName')}
                  >
                    <div className="flex items-center gap-2">
                      Nome Completo
                      {getSortIcon('patientFullName')}
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                    onClick={() => handleSort('patientId')}
                  >
                    <div className="flex items-center gap-2">
                      Prontuário
                      {getSortIcon('patientId')}
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                    onClick={() => handleSort('patientCreatedAt')}
                  >
                    <div className="flex items-center gap-2">
                      Data de Registro
                      {getSortIcon('patientCreatedAt')}
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredPatients.map((patient) => {
                  const age = calculateAge(patient.patientDateOfBirth);
                  
                  return (
                    <tr key={patient.patientId} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        <div className="relative inline-flex items-center gap-2 group">
                          <button
                            type="button"
                            onClick={() => setSelectedPatient(patient)}
                            className="text-left font-semibold text-primary-700 hover:text-primary-900 hover:underline focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 rounded"
                            title="Clique para visualizar este paciente"
                          >
                            {patient.patientFullName}
                          </button>
                          {access.canEditPatient && (
                            <button
                              type="button"
                              onClick={() => setEditingPatient(patient)}
                              className="inline-flex h-6 w-6 items-center justify-center rounded-md border border-gray-200 bg-white text-gray-500 opacity-80 transition hover:border-primary-200 hover:text-primary-700 hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
                              title="Editar paciente"
                            >
                              <Pencil className="h-3.5 w-3.5" />
                            </button>
                          )}
                          <div className="pointer-events-none absolute left-0 top-full z-20 mt-2 hidden w-80 rounded-2xl border border-gray-200 bg-white p-4 text-xs text-gray-700 shadow-xl ring-1 ring-black/5 group-hover:block group-focus-within:block">
                            <div className="flex items-start justify-between gap-3">
                              <div>
                                <div className="text-sm font-semibold text-gray-900">{patient.patientFullName}</div>
                              </div>
                              <span
                                className={`inline-flex items-center rounded-full px-2 py-1 text-[11px] font-semibold ${
                                  getLifecycleStatus(patient) === 'archived'
                                    ? 'bg-amber-100 text-amber-800'
                                    : getLifecycleStatus(patient) === 'inactive'
                                      ? 'bg-gray-100 text-gray-700'
                                      : 'bg-green-100 text-green-700'
                                }`}
                              >
                                {getLifecycleStatus(patient) === 'archived'
                                  ? 'Arquivado'
                                  : getLifecycleStatus(patient) === 'inactive'
                                    ? 'Inativo'
                                    : 'Ativo'}
                              </span>
                            </div>
                            <div className="mt-3 grid grid-cols-1 gap-2 border-t border-gray-100 pt-3">
                              <div><span className="text-gray-500">Sexo:</span> {patient.patientGender || '-'}</div>
                              <div><span className="text-gray-500">Idade:</span> {age} anos</div>
                              <div><span className="text-gray-500">Peso:</span> {(patient.patientWeightKg || 0).toFixed(1)} kg</div>
                              <div><span className="text-gray-500">Altura:</span> {(patient.patientHeightCm || 0).toFixed(0)} cm</div>
                              <div>
                                <span className="text-gray-500">Diagnóstico:</span>{' '}
                                {patient.patientMedicalInfoConditions?.length
                                  ? patient.patientMedicalInfoConditions.join(', ')
                                  : '-'}
                              </div>
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div className="relative inline-block group">
                          <button
                            type="button"
                            onClick={() => setSelectedPatient(patient)}
                            className="font-mono text-gray-900 hover:text-primary-700 hover:underline focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 rounded"
                            title="Clique para visualizar este paciente"
                          >
                            {patient.patientId}
                          </button>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <button
                          type="button"
                          onClick={() => setSelectedPatient(patient)}
                          className="hover:text-primary-700 hover:underline focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 rounded"
                          title="Clique para visualizar este paciente"
                        >
                          {formatDate(patient.patientCreatedAt)}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredPatients.map((patient) => (
              <div key={patient.patientId} className="bg-white p-6 rounded-lg shadow-sm border hover:shadow-md transition-shadow">
                <div className="flex justify-between items-start mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <button
                        type="button"
                        onClick={() => setSelectedPatient(patient)}
                        className="text-left text-lg font-semibold text-gray-900 hover:text-primary-700 hover:underline focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 rounded"
                        title="Clique para visualizar este paciente"
                      >
                        {patient.patientFullName}
                      </button>
                      {access.canEditPatient && (
                        <button
                          type="button"
                          onClick={() => setEditingPatient(patient)}
                          className="inline-flex h-6 w-6 items-center justify-center rounded-md border border-gray-200 bg-white text-gray-500 opacity-80 transition hover:border-primary-200 hover:text-primary-700 hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
                          title="Editar paciente"
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                      )}
                      <LinkedPatientBadge
                        isLinked={!!patient.userId}
                        size="sm"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => setSelectedPatient(patient)}
                      className="mt-1 text-sm text-gray-600 hover:text-primary-700 hover:underline focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 rounded"
                      title="Clique para visualizar este paciente"
                    >
                      Prontuário: {patient.patientId}
                    </button>
                    <div className="mt-1 text-sm text-gray-500">
                      Registro: {formatDate(patient.patientCreatedAt)}
                    </div>
                    <div className="mt-2">
                      {getLifecycleStatus(patient) === 'archived' ? (
                        <span className="inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full bg-amber-100 text-amber-800">
                          Arquivado
                        </span>
                      ) : getLifecycleStatus(patient) === 'inactive' ? (
                        <span className="inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-700">
                          Inativo
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-700">
                          Ativo
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setSelectedPatient(patient)}
                      className="text-blue-600 hover:text-blue-900"
                      title="Visualizar"
                    >
                      <Eye className="w-5 h-5" />
                    </button>
                    {patientViewMode === 'active' && access.canArchivePatient ? (
                      <button
                        onClick={() => {
                          setDeletingPatient(patient);
                          setDeleteReason('');
                        }}
                        className="text-red-600 hover:text-red-900"
                        title="Excluir"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    ) : patientViewMode === 'archived' && access.canRestorePatient ? (
                      <button
                        onClick={() => {
                          setRestoringPatient(patient);
                          setRestoreReason('');
                        }}
                        className="text-amber-700 hover:text-amber-900"
                        title="Restaurar"
                      >
                        <RefreshCw className="w-5 h-5" />
                      </button>
                    ) : null}
                  </div>
                </div>
                
                <p className="text-sm text-gray-500">
                  Clique no nome ou prontuário para abrir o resumo do paciente.
                </p>
              </div>
          ))}
        </div>
      )}

      {/* Modal de Detalhes */}
      {selectedPatient && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-start mb-6">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">
                    {selectedPatient.patientFullName}
                  </h2>
                  <p className="text-gray-600">Prontuário: {selectedPatient.patientId}</p>
                </div>
                <button
                  onClick={() => setSelectedPatient(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-6">
                <button
                  type="button"
                  onClick={() => navigate(buildTenantPath(`${prontuarioPath}?patientId=${encodeURIComponent(selectedPatient.patientId)}`, tenantSlug))}
                  className="rounded-xl border border-gray-200 bg-slate-50 p-4 text-left hover:bg-white hover:border-primary-200 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-xs uppercase tracking-wide text-gray-500">Evoluções</div>
                      <div className="mt-1 text-2xl font-bold text-gray-900">
                        {patientSummary.loading ? '...' : patientSummary.evolutions}
                      </div>
                    </div>
                    <FileText className="h-6 w-6 text-primary-600" />
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => navigate(buildTenantPath(`${prontuarioPath}?patientId=${encodeURIComponent(selectedPatient.patientId)}&view=timeline`, tenantSlug))}
                  className="rounded-xl border border-gray-200 bg-slate-50 p-4 text-left hover:bg-white hover:border-teal-200 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-xs uppercase tracking-wide text-gray-500">Linha do cuidado</div>
                      <div className="mt-1 text-sm font-semibold text-gray-900">Timeline clínica</div>
                    </div>
                    <Activity className="h-6 w-6 text-teal-600" />
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => navigate(buildTenantPath(`${assessmentsPath}?patientId=${encodeURIComponent(selectedPatient.patientId)}`, tenantSlug))}
                  className="rounded-xl border border-gray-200 bg-slate-50 p-4 text-left hover:bg-white hover:border-primary-200 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-xs uppercase tracking-wide text-gray-500">Avaliações</div>
                      <div className="mt-1 text-2xl font-bold text-gray-900">
                        {patientSummary.loading ? '...' : patientSummary.assessments}
                      </div>
                    </div>
                    <Stethoscope className="h-6 w-6 text-primary-600" />
                  </div>
                </button>
                {access.canOpenFinancial && (
                  <button
                    type="button"
                    onClick={() => navigate(buildTenantPath(`/admin/financial-new?patientId=${encodeURIComponent(selectedPatient.patientId)}`, tenantSlug))}
                    className="rounded-xl border border-gray-200 bg-slate-50 p-4 text-left hover:bg-white hover:border-primary-200 transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-xs uppercase tracking-wide text-gray-500">Sessões ativas</div>
                        <div className="mt-1 text-2xl font-bold text-gray-900">
                          {patientSummary.loading ? '...' : patientSummary.activeSessions}
                        </div>
                      </div>
                      <DollarSign className="h-6 w-6 text-primary-600" />
                    </div>
                  </button>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Dados Pessoais</h3>
                  <div className="space-y-2 text-sm">
                    <div>
                      <span className="text-gray-600">CPF:</span>
                      <span className="ml-2 font-medium">{selectedPatient.patientCpf || 'Não informado'}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Data de Nascimento:</span>
                      <span className="ml-2 font-medium">{formatDate(selectedPatient.patientDateOfBirth)}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Sexo:</span>
                      <span className="ml-2 font-medium">{selectedPatient.patientGender || '-'}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Idade:</span>
                      <span className="ml-2 font-medium">{calculateAge(selectedPatient.patientDateOfBirth)} anos</span>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Dados Físicos</h3>
                  <div className="space-y-2 text-sm">
                    <div>
                      <span className="text-gray-600">Altura:</span>
                      <span className="ml-2 font-medium">{(selectedPatient.patientHeightCm || 0).toFixed(0)} cm</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Peso:</span>
                      <span className="ml-2 font-medium">{(selectedPatient.patientWeightKg || 0).toFixed(1)} kg</span>
                    </div>
                    <div>
                      <span className="text-gray-600">IMC:</span>
                      <span className="ml-2 font-medium">
                        {calculateIMC(selectedPatient.patientWeightKg || 0, selectedPatient.patientHeightCm || 0).toFixed(1)}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-600">Classificação:</span>
                      <span className="ml-2 font-medium">
                        {getIMCClassification(calculateIMC(selectedPatient.patientWeightKg || 0, selectedPatient.patientHeightCm || 0))}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="md:col-span-2">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Informações do Sistema</h3>
                  <div className="space-y-2 text-sm">
                    <div>
                      <span className="text-gray-600">Endereço:</span>
                      <span className="ml-2 font-medium">{formatAddress(selectedPatient)}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Diagnóstico:</span>
                      <span className="ml-2 font-medium">{selectedPatient.patientMedicalInfoConditions?.join(", ") || ''}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Data de Registro:</span>
                      <span className="ml-2 font-medium">{formatDate(selectedPatient.patientCreatedAt)}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Status de Ciclo de Vida:</span>
                      <span className="ml-2 font-medium">
                        {getLifecycleStatus(selectedPatient) === 'archived'
                          ? 'Arquivado'
                          : getLifecycleStatus(selectedPatient) === 'inactive'
                            ? 'Inativo'
                            : 'Ativo'}
                      </span>
                    </div>
                    {selectedPatient.patientArchivedAt && (
                      <div>
                        <span className="text-gray-600">Arquivado em:</span>
                        <span className="ml-2 font-medium">{formatDate(selectedPatient.patientArchivedAt, true)}</span>
                      </div>
                    )}
                    {selectedPatient.patientArchivedReason && (
                      <div>
                        <span className="text-gray-600">Motivo do Arquivamento:</span>
                        <span className="ml-2 font-medium">{selectedPatient.patientArchivedReason}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {access.canViewArchivedPatients && (
                <div className="mt-6 border-t pt-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Auditoria de Ciclo de Vida</h3>
                  {loadingLifecycleEvents ? (
                    <p className="text-sm text-gray-500">Carregando eventos...</p>
                  ) : lifecycleEvents.length === 0 ? (
                    <p className="text-sm text-gray-500">Nenhum evento de ciclo de vida registrado para este paciente.</p>
                  ) : (
                    <div className="space-y-2 max-h-56 overflow-y-auto">
                      {lifecycleEvents.map((event) => (
                        <div key={event.eventId} className="rounded-md border border-gray-200 p-3 bg-gray-50">
                          <div className="flex flex-wrap items-center justify-between gap-2">
                            <span className="text-sm font-semibold text-gray-900">
                              {event.action === 'archived' ? 'Arquivado' : event.action === 'restored' ? 'Restaurado' : 'Exclusão Permanente'}
                            </span>
                            <span className="text-xs text-gray-500">{formatDate(event.eventCreatedAt, true)}</span>
                          </div>
                          <div className="mt-1 text-xs text-gray-600">
                            <div>Usuário: {event.actorDisplayName || event.actorEmail || event.actorUserId || 'Não informado'}</div>
                            <div>IP: {event.actorIp || 'Não informado'}</div>
                            {event.reason && <div>Motivo: {event.reason}</div>}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <div className="mt-6 flex justify-end">
                <button
                  onClick={() => setSelectedPatient(null)}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Fechar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Novo Paciente */}
      {access.canCreatePatient && showNewPatientModal && (
        <PatientRegistrationModal
          formData={newPatientForm}
          onCancel={handleCancelNewPatientModal}
          onCepBlur={handleCepBlur}
          onClose={handleCloseNewPatientModal}
          onConsentChange={setConsentGiven}
          onFormChange={updateNewPatientForm}
          onSubmit={handleCreatePatient}
          consentGiven={consentGiven}
          isSubmitting={isSubmitting}
        />
      )}

      {/* Modal de Edição de Paciente */}
      {access.canEditPatient && editingPatient && (
        <EditPatientModal
          patient={editingPatient}
          onClose={() => setEditingPatient(null)}
          onSave={handleSavePatient}
          onDeleteRequest={(patient) => {
            setEditingPatient(null);
            setDeletingPatient(patient);
            setDeleteReason('');
          }}
        />
      )}

      {/* Modal de Confirmação de Exclusão */}
      {access.canArchivePatient && deletingPatient && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center mb-4">
                <div className="flex-shrink-0">
                  <Trash2 className="w-8 h-8 text-red-600" />
                </div>
                <div className="ml-3">
                  <h3 className="text-lg font-semibold text-gray-900">Confirmar Exclusão</h3>
                  <p className="text-sm text-gray-600 mt-1">A ação será registrada com auditoria e IP.</p>
                </div>
              </div>

              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                <p className="text-sm text-gray-900 mb-2">
                  <strong>Paciente:</strong> {deletingPatient.patientFullName}
                </p>
                <p className="text-sm text-gray-900 mb-2">
                  <strong>Prontuário:</strong> {deletingPatient.patientId}
                </p>
                <p className="text-sm text-red-700 mt-3 font-medium">
                  Se houver histórico assistencial (agendamentos concluídos ou prontuários), o paciente será arquivado e removido da lista ativa, sem perda de dados.
                </p>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Motivo da exclusão/arquivamento *
                </label>
                <textarea
                  value={deleteReason}
                  onChange={(e) => setDeleteReason(e.target.value)}
                  rows={3}
                  placeholder="Descreva o motivo (ex.: pedido do paciente, cadastro duplicado, etc.)"
                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                  required
                />
              </div>

              <div className="flex justify-end gap-3">
                <button
                  onClick={() => {
                    setDeletingPatient(null);
                    setDeleteReason('');
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                  disabled={isDeleting}
                >
                  Cancelar
                </button>
                <button
                  onClick={handleDeletePatient}
                  disabled={isDeleting || !deleteReason.trim()}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {isDeleting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Processando...
                    </>
                  ) : (
                    <>
                      <Trash2 className="w-4 h-4" />
                      Confirmar Ação
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Restauração de Paciente */}
      {access.canRestorePatient && restoringPatient && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center mb-4">
                <div className="flex-shrink-0">
                  <RefreshCw className="w-8 h-8 text-amber-600" />
                </div>
                <div className="ml-3">
                  <h3 className="text-lg font-semibold text-gray-900">Restaurar Paciente</h3>
                  <p className="text-sm text-gray-600 mt-1">O paciente voltará para a lista ativa.</p>
                </div>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-gray-900 mb-2">
                  <strong>Paciente:</strong> {restoringPatient.patientFullName}
                </p>
                <p className="text-sm text-gray-900">
                  <strong>Prontuário:</strong> {restoringPatient.patientId}
                </p>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Motivo da restauração
                </label>
                <textarea
                  value={restoreReason}
                  onChange={(e) => setRestoreReason(e.target.value)}
                  rows={3}
                  placeholder="Ex.: retorno ao atendimento, ajuste administrativo..."
                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                />
              </div>

              <div className="flex justify-end gap-3">
                <button
                  onClick={() => {
                    setRestoringPatient(null);
                    setRestoreReason('');
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                  disabled={isRestoring}
                >
                  Cancelar
                </button>
                <button
                  onClick={handleRestorePatient}
                  disabled={isRestoring}
                  className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {isRestoring ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Restaurando...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="w-4 h-4" />
                      Confirmar Restauração
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PatientRegistrationPage;
